/**
 * Typeahead — a tiny generic combobox for filter rows.
 *
 * Backed by an in-memory list (caller passes ``options``). Items are
 * pre-sorted alphabetically by the value of ``getLabel`` so an empty
 * input shows everything in order, and typing filters the list to
 * substring matches on the label / secondary fields.
 *
 * Used wherever we previously had a ``<select>`` with hundreds of
 * options (e.g. the investor / project filters on /matches), where
 * the native select offered no filtering and arbitrary ordering.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';


interface TypeaheadProps<T> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getLabel: (item: T) => string;
  getSecondary?: (item: T) => string | null | undefined;
  getKey: (item: T) => string | number;
  placeholder?: string;
  emptyLabel?: string;  // shown when value is null (e.g. "All investors")
  disabled?: boolean;
  className?: string;
}


export default function Typeahead<T>({
  options,
  value,
  onChange,
  getLabel,
  getSecondary,
  getKey,
  placeholder = 'Type to search…',
  emptyLabel = 'All',
  disabled = false,
  className = '',
}: TypeaheadProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  // Keep the input text in sync with the selected value when an item
  // is picked from the dropdown. When no value is set, keep whatever
  // the user typed so they can keep narrowing.
  useEffect(() => {
    if (value !== null) {
      setQuery(getLabel(value));
    }
  }, [value, getLabel]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Alphabetise once, filter live. Substring match on label + secondary.
  const filtered = useMemo(() => {
    const sorted = options.slice().sort((a, b) =>
      getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' }),
    );
    if (!query.trim()) return sorted;
    const q = query.toLowerCase().trim();
    return sorted.filter((item) => {
      const lbl = getLabel(item).toLowerCase();
      if (lbl.includes(q)) return true;
      const sec = getSecondary?.(item)?.toLowerCase();
      return sec ? sec.includes(q) : false;
    });
  }, [options, query, getLabel, getSecondary]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <div
        className={`flex items-center w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white ${
          disabled ? 'opacity-50' : 'focus-within:ring-1 focus-within:ring-blue-500'
        }`}
      >
        <input
          type="text"
          disabled={disabled}
          value={query}
          placeholder={value === null ? emptyLabel : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // If the user deletes everything, clear the selection.
            if (!e.target.value && value !== null) onChange(null);
          }}
          className="flex-1 bg-transparent outline-none placeholder-gray-400"
        />
        {value !== null ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-700"
            title="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No matches.</div>
          ) : (
            <ul>
              {filtered.slice(0, 200).map((item) => {
                const key = getKey(item);
                const lbl = getLabel(item);
                const sec = getSecondary?.(item);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onChange(item);
                        setQuery(lbl);
                        setOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                    >
                      <span className="block truncate">{lbl}</span>
                      {sec && (
                        <span className="block truncate text-xs text-gray-500">{sec}</span>
                      )}
                    </button>
                  </li>
                );
              })}
              {filtered.length > 200 && (
                <li className="px-3 py-1 text-xs text-gray-400 italic">
                  Showing first 200 — narrow your search to see more.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
